<template>
  <form @submit="onSubmit" novalidate class="usa-form usa-form--large">
    <fieldset class="usa-fieldset">
      <p>
        Required fields are marked with an asterisk (<abbr
          title="required"
          class="usa-hint usa-hint--required"
        >*</abbr
        >).
      </p>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displayTypeError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displayTypeError }" for="contact_type">
          Inquiry type
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displayTypeError" class="usa-error-message">{{ typeError }}</span>
        <select
          v-model="type"
          @blur="typeHandleBlur"
          class="usa-select"
          id="contact_type"
          name="type"
          required
          aria-required="true"
        >
          <option value>- Select -</option>
          <option>State inquiry</option>
          <option>Community inquiry</option>
          <option>General inquiry</option>
          <option>Clean Heavy-Duty Vehicle Technical Assistance</option>
          <option>Clean School Bus Technical Assistance</option>
          <option>Low-No Emissions Transit Vehicle Technical Assistance</option>
          <option>Tribal Nations Technical Assistance</option>
          <option>Reporting (EV-ChART)</option>
        </select>
      </div>

      <template v-if="type === 'Low-No Emissions Transit Vehicle Technical Assistance'">
        <div class="usa-alert usa-alert--warning usa-alert--no-icon">
          <div class="usa-alert__body">
            <p class="usa-alert__text"><a href="/transit-agencies">Transit vehicle technical assistance</a> is only available for eligible entities currently receiving Low-No funds, planning to apply for Low-No funds, or using other Federal Transit Administration (FTA) program funds for clean transit buses.</p>
          </div>
        </div>
      </template>

      <div v-if="type === 'Low-No Emissions Transit Vehicle Technical Assistance'" :class="{ 'usa-form-group': true, 'usa-form-group--error': displayFundingError }">
        <fieldset class="usa-fieldset">
          <legend :class="{ 'usa-label': true, 'usa-label--error': displayFundingError }">
            Funding
            <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
          </legend>
          <span v-if="displayFundingError" class="usa-error-message">{{ fundingError }}</span>
          <div class="usa-checkbox">
            <input
              class="usa-checkbox__input"
              id="receive-low-no-funds"
              type="checkbox"
              name="funding"
              value="receive-low-no-funds"
              v-model="funding"
            />
            <label
              class="usa-checkbox__label"
              for="receive-low-no-funds"
            >Currently receiving Low-No funds</label
            >
          </div>
          <div class="usa-checkbox">
            <input
              class="usa-checkbox__input"
              id="apply-low-no-funds"
              type="checkbox"
              name="funding"
              value="apply-low-no-funds"
              v-model="funding"
            />
            <label
              class="usa-checkbox__label"
              for="apply-low-no-funds"
            >Planning to apply for Low-No funds</label
            >
          </div>
          <div class="usa-checkbox">
            <input
              class="usa-checkbox__input"
              id="utilizing-other"
              type="checkbox"
              name="funding"
              value="utilizing-other"
              v-model="funding"
            />
            <label
              class="usa-checkbox__label"
              for="utilizing-other"
            >Using other FTA program funds for clean transit buses</label
            >
          </div>
          <div class="usa-checkbox">
            <input
              class="usa-checkbox__input"
              id="none"
              type="checkbox"
              name="funding"
              value="none"
              v-model="funding"
            />
            <label
              class="usa-checkbox__label"
              for="none"
            >None of the above</label
            >
          </div>
        </fieldset>
      </div>

      <template v-if="type === 'Community inquiry'">
        <div class="usa-alert usa-alert--warning usa-alert--no-icon">
          <div class="usa-alert__body">
            <p class="usa-alert__text"><a href="/communities">Communities technical assistance</a> is available to communities at all stages of interest, planning, and deployment of publicly accessible zero-emission vehicle charging and refueling infrastructure. To better assist your technical assistance request, please indicate whether you are receiving Charging and Fueling Infrastructure (CFI) Discretionary Grant Program funds.</p>
          </div>
        </div>
      </template>

      <div v-if="type === 'Community inquiry'" :class="{ 'usa-form-group': true, 'usa-form-group--error': displayFundingError }">
        <fieldset class="usa-fieldset">
          <legend :class="{ 'usa-label': true, 'usa-label--error': displayFundingError }">
            Funding
            <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
          </legend>
          <span v-if="displayFundingError" class="usa-error-message">{{ fundingError }}</span>
          <div class="usa-checkbox">
            <input
              class="usa-checkbox__input"
              id="receiving-cfi-funds"
              type="checkbox"
              name="funding"
              value="receiving-cfi-funds"
              v-model="funding"
            />
            <label
              class="usa-checkbox__label"
              for="receiving-cfi-funds"
            >Currently receiving CFI funds</label
            >
          </div>
          <div class="usa-checkbox">
            <input
              class="usa-checkbox__input"
              id="apply-cfi-funds"
              type="checkbox"
              name="funding"
              value="apply-cfi-funds"
              v-model="funding"
            />
            <label
              class="usa-checkbox__label"
              for="apply-cfi-funds"
            >Planning to apply for CFI funds</label
            >
          </div>
          <div class="usa-checkbox">
            <input
              class="usa-checkbox__input"
              id="using-other"
              type="checkbox"
              name="funding"
              value="using-other"
              v-model="funding"
            />
            <label
              class="usa-checkbox__label"
              for="using-other"
            >Using other federal program funds for community charging</label
            >
          </div>
          <div class="usa-checkbox">
            <input
              class="usa-checkbox__input"
              id="none"
              type="checkbox"
              name="funding"
              value="none"
              v-model="funding"
            />
            <label
              class="usa-checkbox__label"
              for="none"
            >None of the above</label
            >
          </div>
        </fieldset>
      </div>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displayNameError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displayNameError }" for="contact_name">
          Name
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displayNameError" class="usa-error-message">{{ nameError }}</span>
        <input
          v-model="name"
          @blur="nameHandleBlur"
          class="usa-input"
          id="contact_name"
          name="name"
          type="text"
          required
          aria-required="true"
        />
      </div>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displayEmailError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displayEmailError }" for="contact_email">
          Email
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displayEmailError" class="usa-error-message">{{ emailError }}</span>
        <input
          v-model="email"
          @blur="emailHandleBlur"
          class="usa-input"
          id="contact_email"
          name="email"
          type="email"
          required
          aria-required="true"
        />
      </div>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displaySubjectError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displaySubjectError }" for="contact_subject">
          Subject
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displaySubjectError" class="usa-error-message">{{ subjectError }}</span>
        <input
          v-model="subject"
          @blur="subjectHandleBlur"
          class="usa-input"
          id="contact_subject"
          name="subject"
          type="text"
          required
          aria-required="true"
        />
      </div>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displayMessageError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displayMessageError }" for="contact_message">
          Message
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displayMessageError" class="usa-error-message">{{ messageError }}</span>
        <textarea
          v-model="message"
          @blur="messageHandleBlur"
          class="usa-textarea"
          id="contact_message"
          name="message"
          rows="6"
          required
          aria-required="true" />
      </div>

      <button
        type="submit"
        class="usa-button"
        :disabled="isSubmitting"
      >
        {{ isSubmitting ? 'Sending...' : 'Send' }}
      </button>
    </fieldset>
  </form>
</template>

<script>
import { computed, watch } from 'vue';
import { useForm, useField } from 'vee-validate';
import { array, object, string } from 'yup';
import Swal from 'sweetalert2/dist/sweetalert2';
import ky from 'ky';

export default {
  setup() {
    const schema = object({
      type: string().label('Inquiry type').required(),
      funding: array()
        .of(string())
        .label('Funding')
        .when('type', ([type], fieldSchema) => {
          if (type === 'Low-No Emissions Transit Vehicle Technical Assistance' || type === 'Community inquiry') {
            return fieldSchema
              .required()
              .min(1)
              .test('none-check', "None of the above can't be selected with other options", (value) => {
                if (value && value.includes('none') && value.length > 1) {
                  return false;
                }

                return true;
              });
          }

          return fieldSchema;
        }),
      name: string().label('Name').required(),
      email: string().label('Email').required().email(),
      subject: string().label('Subject').required(),
      message: string().label('Message').required(),
    });

    const { handleSubmit, isSubmitting } = useForm({
      validationSchema: schema,
    });

    function fundingMsg(fundingArr) {
      const fundingType = {
        'receive-low-no-funds': 'Currently receiving Low-No funds',
        'apply-low-no-funds': 'Planning to apply for Low-No funds',
        'utilizing-other': 'Using other FTA program funds for clean transit buses',
        'receiving-cfi-funds': 'Currently receiving CFI funds',
        'apply-cfi-funds': 'Planning to apply for CFI funds',
        'using-other': 'Using other federal program funds for community charging',
        none: 'None',
      };
      let msg = 'Funding: ';
      fundingArr.forEach((choice) => {
        msg += `${fundingType[choice]}. `;
      });
      return msg;
    }

    const onSubmit = handleSubmit(async (values, { resetForm }) => {
      const projectNames = {
        'Community inquiry': 'community_technical_assistance',
        'Clean Heavy-Duty Vehicle Technical Assistance': 'clean_hd_vehicles',
        'Clean School Bus Technical Assistance': 'clean_school_bus',
        'Low-No Emissions Transit Vehicle Technical Assistance': 'transit_technical_assistance',
        'Tribal Nations Technical Assistance': 'tribal_technical_assistance',
        'Reporting (EV-ChART)': 'evchart',
      };

      const projectName = projectNames[values.type] !== undefined ? projectNames[values.type] : 'drive_electric';

      const data = {
        contact: {
          ...values,
          // eslint-disable-next-line no-nested-ternary
          project: projectName,
        },
      };

      delete data.contact.type;
      delete data.contact.funding;
      const fundingType = fundingMsg(values.funding);
      data.contact.subject = `[${values.type}] ${values.subject}`;

      if (values.type === 'Low-No Emissions Transit Vehicle Technical Assistance' || values.type === 'Community inquiry') {
        data.contact.message = `${fundingType}\n\n${values.message}`;
      }

      try {
        await ky.post(import.meta.env.VITE_CONTACTOR_API_URL, {
          searchParams: {
            api_key: import.meta.env.VITE_NREL_API_KEY,
          },
          json: data,
        });

        resetForm();

        Swal.fire({
          text: "Thanks for sending your message. We'll be in touch.",
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);

        Swal.fire({
          text: 'Sending your message unexpectedly failed. Please try again.',
        });
      }
    });

    const {
      value: type,
      errorMessage: typeError,
      handleBlur: typeHandleBlur,
      meta: typeMeta,
    } = useField('type');
    const {
      value: funding,
      errorMessage: fundingError,
      handleBlur: fundingHandleBlur,
      meta: fundingMeta,
    } = useField('funding', undefined, { initialValue: [] });
    const {
      value: name,
      errorMessage: nameError,
      handleBlur: nameHandleBlur,
      meta: nameMeta,
    } = useField('name');
    const {
      value: email,
      errorMessage: emailError,
      handleBlur: emailHandleBlur,
      meta: emailMeta,
    } = useField('email');
    const {
      value: subject,
      errorMessage: subjectError,
      handleBlur: subjectHandleBlur,
      meta: subjectMeta,
    } = useField('subject');
    const {
      value: message,
      errorMessage: messageError,
      handleBlur: messageHandleBlur,
      meta: messageMeta,
    } = useField('message');

    const displayTypeError = computed(() => typeError.value && typeMeta.touched);
    const displayFundingError = computed(() => fundingError.value && fundingMeta.touched);
    const displayNameError = computed(() => nameError.value && nameMeta.touched);
    const displayEmailError = computed(() => emailError.value && emailMeta.touched);
    const displaySubjectError = computed(() => subjectError.value && subjectMeta.touched);
    const displayMessageError = computed(() => messageError.value && messageMeta.touched);

    watch(funding, (newValue, prevValue) => {
      // If the "none" option is checked and it wasn't previously checked, then
      // uncheck any other options.
      if (newValue.includes('none') && newValue.length > 1 && !prevValue.includes('none')) {
        funding.value = ['none'];

      // If the "none" option was previously checked, and a new, non-"none"
      // option is being checked, then uncheck the "none" option.
      } else if (newValue.includes('none') && newValue.length > 1 && prevValue.includes('none') && prevValue.length === 1) {
        funding.value = funding.value.filter((o) => o !== 'none');
      }
    });

    if (window.location.search.includes('inquiry=hdvehicle')) {
      type.value = 'Clean Heavy-Duty Vehicle Technical Assistance';
    } else if (window.location.search.includes('inquiry=schoolbus')) {
      type.value = 'Clean School Bus Technical Assistance';
    } else if (window.location.search.includes('inquiry=transit')) {
      type.value = 'Low-No Emissions Transit Vehicle Technical Assistance';
    } else if (window.location.search.includes('inquiry=community')) {
      type.value = 'Community inquiry';
    } else if (window.location.search.includes('inquiry=tribal')) {
      type.value = 'Tribal Nations Technical Assistance';
    } else if (window.location.search.includes('inquiry=general')) {
      type.value = 'General inquiry';
    } else if (window.location.search.includes('inquiry=evchart')) {
      type.value = 'Reporting (EV-ChART)';
    }

    return {
      type,
      typeError,
      typeHandleBlur,
      displayTypeError,
      funding,
      fundingError,
      fundingHandleBlur,
      displayFundingError,
      fundingMsg,
      name,
      nameError,
      nameHandleBlur,
      displayNameError,
      email,
      emailError,
      emailHandleBlur,
      displayEmailError,
      subject,
      subjectError,
      subjectHandleBlur,
      displaySubjectError,
      message,
      messageError,
      messageHandleBlur,
      displayMessageError,
      isSubmitting,
      onSubmit,
    };
  },
};
</script>
